import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Save, RefreshCw, Bell, Database, Shield, Mail, Globe, FileText, Download, Trash2 } from 'lucide-react';
import { graphqlService } from '../services/graphqlService';

const SystemSettings = ({ onBack }) => {
  const [settings, setSettings] = useState({
    // General Settings (Read-only Application Info)
    applicationName: 'MeritIQ',
    applicationDescription: 'TruMerit Training Portal',
    applicationReleaseVersion: '1.0.0',
    releaseNotes: 'Initial release of MeritIQ Training Portal. Features include module management, user management, training scheduling, progress tracking, and analytics.',
    timezone: 'UTC',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    
    // Notification Settings
    emailNotifications: true,
    pushNotifications: false,
    notificationFrequency: 'immediate',
    notifyOnModuleAssignment: true,
    notifyOnUpcomingTraining: true,
    
    // System Settings
    autoSave: true,
    sessionTimeout: 30,
    maxFileUploadSize: 10,
    enableAnalytics: true,
    
    // Security Settings
    requirePasswordChange: false,
    passwordMinLength: 8,
    enableTwoFactor: false,
    sessionTimeoutEnabled: true,
    
    // Data Management
    autoBackup: true,
    backupFrequency: 'daily',
    retentionPeriod: 90
  });

  const [activeTab, setActiveTab] = useState('general');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState('all'); // 'all', 'error', 'warning', 'info'

  const loadLogs = useCallback(() => {
    const savedLogs = localStorage.getItem('systemLogs');
    if (savedLogs) {
      try {
        const parsed = JSON.parse(savedLogs);
        setLogs(parsed);
      } catch (error) {
        console.error('Error loading logs:', error);
        setLogs([]);
      }
    } else {
      // Initialize with sample logs if none exist
      const initialLogs = [
        {
          id: Date.now() - 4,
          timestamp: new Date(Date.now() - 3600000).toISOString(),
          level: 'info',
          message: 'System initialized successfully',
          source: 'System'
        },
        {
          id: Date.now() - 3,
          timestamp: new Date(Date.now() - 1800000).toISOString(),
          level: 'info',
          message: 'User authentication successful',
          source: 'Auth'
        },
        {
          id: Date.now() - 2,
          timestamp: new Date(Date.now() - 900000).toISOString(),
          level: 'warning',
          message: 'Session timeout warning',
          source: 'Session'
        },
        {
          id: Date.now() - 1,
          timestamp: new Date().toISOString(),
          level: 'info',
          message: 'Settings loaded from storage',
          source: 'Settings'
        }
      ];
      setLogs(initialLogs);
      localStorage.setItem('systemLogs', JSON.stringify(initialLogs));
    }
  }, []);

  useEffect(() => {
    // Load settings from database
    const loadSettings = async () => {
      try {
        // Load each setting key from database
        const settingKeys = [
          'timezone', 'dateFormat', 'timeFormat',
          'emailNotifications', 'pushNotifications', 'notificationFrequency',
          'notifyOnModuleAssignment', 'notifyOnUpcomingTraining',
          'autoSave', 'sessionTimeout', 'maxFileUploadSize', 'enableAnalytics',
          'requirePasswordChange', 'passwordMinLength', 'enableTwoFactor', 'sessionTimeoutEnabled',
          'autoBackup', 'backupFrequency', 'retentionPeriod'
        ];

        const loadedSettings = {};
        for (const key of settingKeys) {
          try {
            const setting = await graphqlService.getSetting(key);
            if (setting) {
              loadedSettings[key] = setting.value;
            }
          } catch (error) {
            // Setting doesn't exist, use default
          }
        }

        // Preserve read-only application info fields
        setSettings(prev => ({
          ...prev,
          ...loadedSettings,
          applicationName: prev.applicationName, // Keep read-only value
          applicationDescription: prev.applicationDescription, // Keep read-only value
          applicationReleaseVersion: prev.applicationReleaseVersion, // Keep read-only value
          releaseNotes: prev.releaseNotes // Keep read-only value
        }));
      } catch (error) {
        console.error('Error loading settings:', error);
      }
    };

    loadSettings();
    
    // Load logs from localStorage (logs can stay in localStorage for now)
    loadLogs();
  }, [loadLogs]);


  const handleClearLogs = () => {
    if (window.confirm('Are you sure you want to clear all logs? This action cannot be undone.')) {
      localStorage.removeItem('systemLogs');
      setLogs([]);
    }
  };

  const handleExportLogs = () => {
    const filteredLogs = getFilteredLogs();
    const logText = filteredLogs.map(log => 
      `[${new Date(log.timestamp).toLocaleString()}] [${log.level.toUpperCase()}] [${log.source}] ${log.message}`
    ).join('\n');
    
    const blob = new Blob([logText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `meritiq-logs-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const getFilteredLogs = () => {
    if (logFilter === 'all') {
      return logs;
    }
    return logs.filter(log => log.level === logFilter);
  };

  const getLogLevelColor = (level) => {
    switch (level) {
      case 'error':
        return '#dc2626';
      case 'warning':
        return '#f59e0b';
      case 'info':
        return '#3b82f6';
      default:
        return '#6b7280';
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');
    
    try {
      // Save each setting to database
      const settingKeys = [
        'timezone', 'dateFormat', 'timeFormat',
        'emailNotifications', 'pushNotifications', 'notificationFrequency',
        'notifyOnModuleAssignment', 'notifyOnUpcomingTraining',
        'autoSave', 'sessionTimeout', 'maxFileUploadSize', 'enableAnalytics',
        'requirePasswordChange', 'passwordMinLength', 'enableTwoFactor', 'sessionTimeoutEnabled',
        'autoBackup', 'backupFrequency', 'retentionPeriod'
      ];

      for (const key of settingKeys) {
        if (settings[key] !== undefined) {
          await graphqlService.createOrUpdateSetting({
            key: key,
            value: settings[key],
            description: `System setting for ${key}`
          });
        }
      }

      setIsSaving(false);
      setSaveMessage('Settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setIsSaving(false);
      setSaveMessage('Failed to save settings. Please try again.');
      setTimeout(() => setSaveMessage(''), 3000);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all settings to default values? This action cannot be undone.')) {
      localStorage.removeItem('systemSettings');
      window.location.reload();
    }
  };


  const tabs = [
    { id: 'general', label: 'General', icon: Globe },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    { id: 'system', label: 'System', icon: Database },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'data', label: 'Data Management', icon: Database },
    { id: 'logs', label: 'Log Viewer', icon: FileText }
  ];

  return (
    <div className="container">
      <div className="module-form-header">
        <button onClick={onBack} className="btn btn-secondary back-to-dashboard">
          <ArrowLeft size={16} style={{ marginRight: '8px' }} />
          Back to Dashboard
        </button>
      </div>

      <div className="module-management-header">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: '#0477aa' }}>System Settings</h1>
          <p className="text-gray-600">Configure system settings and preferences</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          {saveMessage && (
            <span style={{ color: '#059669', fontWeight: 500 }}>{saveMessage}</span>
          )}
          <button onClick={handleSave} className="btn btn-primary" disabled={isSaving}>
            <Save size={16} style={{ marginRight: '8px' }} />
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>

      <div className="form-section" style={{ marginTop: '2rem' }}>
        {/* Tabs */}
        <div className="sections-tabs" style={{ marginBottom: '2rem' }}>
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <div
                key={tab.id}
                className={`tab ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                style={{ cursor: 'pointer' }}
              >
                <Icon size={16} />
                <span>{tab.label}</span>
              </div>
            );
          })}
        </div>

        {/* General Settings */}
        {activeTab === 'general' && (
          <div className="form-grid">
            <div className="form-group">
              <label>Application Name</label>
              <input
                type="text"
                value={settings.applicationName}
                readOnly
                disabled
                style={{ 
                  background: '#f3f4f6', 
                  cursor: 'not-allowed',
                  color: '#6b7280'
                }}
                placeholder="MeritIQ"
              />
            </div>

            <div className="form-group">
              <label>Application Description</label>
              <input
                type="text"
                value={settings.applicationDescription}
                readOnly
                disabled
                style={{ 
                  background: '#f3f4f6', 
                  cursor: 'not-allowed',
                  color: '#6b7280'
                }}
                placeholder="TruMerit Training Portal"
              />
            </div>

            <div className="form-group">
              <label>Application Release Version</label>
              <input
                type="text"
                value={settings.applicationReleaseVersion}
                readOnly
                disabled
                style={{ 
                  background: '#f3f4f6', 
                  cursor: 'not-allowed',
                  color: '#6b7280'
                }}
                placeholder="1.0.0"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label>Release Notes</label>
              <textarea
                value={settings.releaseNotes}
                readOnly
                disabled
                rows={6}
                style={{ 
                  background: '#f3f4f6', 
                  cursor: 'not-allowed',
                  color: '#6b7280',
                  resize: 'none'
                }}
                placeholder="Release notes will appear here..."
              />
            </div>

            <div className="form-group">
              <label>Timezone</label>
              <select
                value={settings.timezone}
                onChange={(e) => handleChange('timezone', e.target.value)}
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time (ET)</option>
                <option value="America/Chicago">Central Time (CT)</option>
                <option value="America/Denver">Mountain Time (MT)</option>
                <option value="America/Los_Angeles">Pacific Time (PT)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Date Format</label>
              <select
                value={settings.dateFormat}
                onChange={(e) => handleChange('dateFormat', e.target.value)}
              >
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>

            <div className="form-group">
              <label>Time Format</label>
              <select
                value={settings.timeFormat}
                onChange={(e) => handleChange('timeFormat', e.target.value)}
              >
                <option value="12h">12 Hour</option>
                <option value="24h">24 Hour</option>
              </select>
            </div>
          </div>
        )}

        {/* Notification Settings */}
        {activeTab === 'notifications' && (
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) => handleChange('emailNotifications', e.target.checked)}
                />
                <span>Enable Email Notifications</span>
              </label>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.pushNotifications}
                  onChange={(e) => handleChange('pushNotifications', e.target.checked)}
                />
                <span>Enable Push Notifications</span>
              </label>
            </div>

            <div className="form-group">
              <label>Notification Frequency</label>
              <select
                value={settings.notificationFrequency}
                onChange={(e) => handleChange('notificationFrequency', e.target.value)}
              >
                <option value="immediate">Immediate</option>
                <option value="hourly">Hourly Digest</option>
                <option value="daily">Daily Digest</option>
                <option value="weekly">Weekly Digest</option>
              </select>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.notifyOnModuleAssignment}
                  onChange={(e) => handleChange('notifyOnModuleAssignment', e.target.checked)}
                />
                <span>Notify users when modules are assigned</span>
              </label>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.notifyOnUpcomingTraining}
                  onChange={(e) => handleChange('notifyOnUpcomingTraining', e.target.checked)}
                />
                <span>Notify users about upcoming training (48 hours before)</span>
              </label>
            </div>
          </div>
        )}

        {/* System Settings */}
        {activeTab === 'system' && (
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.autoSave}
                  onChange={(e) => handleChange('autoSave', e.target.checked)}
                />
                <span>Enable Auto-Save</span>
              </label>
            </div>

            <div className="form-group">
              <label>Session Timeout (minutes)</label>
              <input
                type="number"
                value={settings.sessionTimeout}
                onChange={(e) => handleChange('sessionTimeout', parseInt(e.target.value) || 30)}
                min="5"
                max="120"
              />
            </div>

            <div className="form-group">
              <label>Max File Upload Size (MB)</label>
              <input
                type="number"
                value={settings.maxFileUploadSize}
                onChange={(e) => handleChange('maxFileUploadSize', parseInt(e.target.value) || 10)}
                min="1"
                max="100"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.enableAnalytics}
                  onChange={(e) => handleChange('enableAnalytics', e.target.checked)}
                />
                <span>Enable Analytics Tracking</span>
              </label>
            </div>
          </div>
        )}

        {/* Security Settings */}
        {activeTab === 'security' && (
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.requirePasswordChange}
                  onChange={(e) => handleChange('requirePasswordChange', e.target.checked)}
                />
                <span>Require Password Change on First Login</span>
              </label>
            </div>

            <div className="form-group">
              <label>Minimum Password Length</label>
              <input
                type="number"
                value={settings.passwordMinLength}
                onChange={(e) => handleChange('passwordMinLength', parseInt(e.target.value) || 8)}
                min="6"
                max="20"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.enableTwoFactor}
                  onChange={(e) => handleChange('enableTwoFactor', e.target.checked)}
                />
                <span>Enable Two-Factor Authentication</span>
              </label>
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.sessionTimeoutEnabled}
                  onChange={(e) => handleChange('sessionTimeoutEnabled', e.target.checked)}
                />
                <span>Enable Session Timeout</span>
              </label>
            </div>
          </div>
        )}

        {/* Data Management */}
        {activeTab === 'data' && (
          <div className="form-grid">
            <div className="form-group" style={{ gridColumn: '1 / -1' }}>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={settings.autoBackup}
                  onChange={(e) => handleChange('autoBackup', e.target.checked)}
                />
                <span>Enable Automatic Backups</span>
              </label>
            </div>

            <div className="form-group">
              <label>Backup Frequency</label>
              <select
                value={settings.backupFrequency}
                onChange={(e) => handleChange('backupFrequency', e.target.value)}
              >
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
                <option value="monthly">Monthly</option>
              </select>
            </div>

            <div className="form-group">
              <label>Data Retention Period (days)</label>
              <input
                type="number"
                value={settings.retentionPeriod}
                onChange={(e) => handleChange('retentionPeriod', parseInt(e.target.value) || 90)}
                min="30"
                max="365"
              />
            </div>

            <div className="form-group" style={{ gridColumn: '1 / -1', marginTop: '2rem' }}>
              <div style={{ 
                padding: '1.5rem', 
                background: '#fef2f2', 
                border: '1px solid #fecaca', 
                borderRadius: '8px' 
              }}>
                <h3 style={{ color: '#dc2626', marginBottom: '1rem', fontWeight: 600 }}>
                  Dangerous Zone
                </h3>
                <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                  <button
                    onClick={handleReset}
                    className="btn btn-secondary"
                    style={{ background: '#f59e0b', color: 'white' }}
                  >
                    <RefreshCw size={16} style={{ marginRight: '8px' }} />
                    Reset to Defaults
                  </button>
                </div>
                <p style={{ color: '#991b1b', fontSize: '0.875rem', marginTop: '1rem' }}>
                  Warning: These actions cannot be undone. Use with extreme caution.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Log Viewer */}
        {activeTab === 'logs' && (
          <div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '1.5rem',
              flexWrap: 'wrap',
              gap: '1rem'
            }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ fontWeight: 600, color: '#374151' }}>Filter by Level:</label>
                <select
                  value={logFilter}
                  onChange={(e) => setLogFilter(e.target.value)}
                  style={{
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    background: 'white',
                    cursor: 'pointer'
                  }}
                >
                  <option value="all">All Logs</option>
                  <option value="error">Errors</option>
                  <option value="warning">Warnings</option>
                  <option value="info">Info</option>
                </select>
                <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  {getFilteredLogs().length} log{getFilteredLogs().length !== 1 ? 's' : ''}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button
                  onClick={loadLogs}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                >
                  <RefreshCw size={16} style={{ marginRight: '8px' }} />
                  Refresh
                </button>
                <button
                  onClick={handleExportLogs}
                  className="btn btn-secondary"
                  style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
                >
                  <Download size={16} style={{ marginRight: '8px' }} />
                  Export Logs
                </button>
                <button
                  onClick={handleClearLogs}
                  className="btn btn-secondary"
                  style={{ 
                    fontSize: '0.875rem', 
                    padding: '0.5rem 1rem',
                    background: '#dc2626',
                    color: 'white',
                    border: 'none'
                  }}
                >
                  <Trash2 size={16} style={{ marginRight: '8px' }} />
                  Clear Logs
                </button>
              </div>
            </div>

            <div style={{
              background: '#1f2937',
              borderRadius: '8px',
              padding: '1rem',
              maxHeight: '600px',
              overflowY: 'auto',
              fontFamily: 'monospace',
              fontSize: '0.875rem'
            }}>
              {getFilteredLogs().length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {getFilteredLogs().map((log) => (
                    <div
                      key={log.id}
                      style={{
                        padding: '0.75rem',
                        background: '#111827',
                        borderRadius: '4px',
                        borderLeft: `3px solid ${getLogLevelColor(log.level)}`,
                        color: '#e5e7eb',
                        lineHeight: '1.5'
                      }}
                    >
                      <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                        <span style={{ color: getLogLevelColor(log.level), fontWeight: 600 }}>
                          [{log.level.toUpperCase()}]
                        </span>
                        <span style={{ color: '#9ca3af' }}>
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                        <span style={{ color: '#6b7280' }}>
                          [{log.source}]
                        </span>
                      </div>
                      <div style={{ color: '#e5e7eb', marginTop: '0.25rem' }}>
                        {log.message}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '3rem', 
                  color: '#6b7280' 
                }}>
                  No logs found. Logs will appear here as system events occur.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemSettings;

